/**
 * Describe a rule in words (ALC-4564, W2-2): the shapes and the stable codes of the proposal.
 *
 * A person with `workflow:manage` writes what a rule should do; an AI reads it into `bill_updated`
 * conditions taken only from the closed vocabulary (`getWorkflowConditionFieldDefinitions`), with
 * the one action `submit_for_approval`. The endpoint answers with a DRAFT the builder opens
 * prefilled, or with a REFUSAL that names the part it could not use. It never saves anything.
 *
 * All or nothing: if any part of the text cannot be expressed, there is no draft at all. A partial
 * draft would be a looser rule than the person asked for, and a looser rule submits bills the
 * person did not mean to submit.
 *
 * Every code below has pt-BR and en copy in {@link WORKFLOW_DESCRIPTION_ERROR_MESSAGES}.
 */

import type { WorkflowOriginKind } from './generated/enums.js';
import type {
  BillWorkflowConditionOperator,
  CreateWorkflowActionPayload,
  CreateWorkflowConditionPayload,
  WorkflowTriggerTypeCode,
} from './wf.js';

/** The longest description the endpoint accepts. Same limit as the origin text it is stored as. */
export const WORKFLOW_DESCRIPTION_MAX_LENGTH = 2000;

/** The most parts or candidates one refusal lists. */
export const WORKFLOW_DESCRIPTION_MAX_LISTED = 5;

/**
 * The entity-kind condition fields a description can name, and so the ones the service can resolve
 * from a name to an id. Any other entity-kind field is out of the compiler's vocabulary until a
 * resolver exists for it. `segment` is not a field of the registry: a custom segment is the field
 * `segment[<id>]`, and the model names it with `segmentName`.
 */
export const WORKFLOW_DESCRIPTION_ENTITY_FIELDS = [
  'vendor',
  'cost_center',
  'department',
  'gl_account',
  'managerial_account',
] as const;
export type WorkflowDescriptionEntityField = (typeof WORKFLOW_DESCRIPTION_ENTITY_FIELDS)[number];

/** The pseudo field the model uses for a custom segment, together with `segmentName`. */
export const WORKFLOW_DESCRIPTION_SEGMENT_FIELD = 'segment';

/** The value kinds a description can express. Profiles and allocation targets need their own editor. */
export const WORKFLOW_DESCRIPTION_VALUE_KINDS = [
  'entity',
  'number',
  'text',
  'date',
  'enum',
  'boolean',
  'service_type',
] as const;
export type WorkflowDescriptionValueKind = (typeof WORKFLOW_DESCRIPTION_VALUE_KINDS)[number];

export const WorkflowDescriptionErrorCode = {
  /** The model reported words the rule cannot express (a second action, a schedule, a field the rule lacks). */
  UNSUPPORTED: 'WORKFLOW_DESCRIPTION_UNSUPPORTED',
  /** The model used a field, operator or action outside the vocabulary. */
  OUT_OF_VOCABULARY: 'WORKFLOW_DESCRIPTION_OUT_OF_VOCABULARY',
  /** A number or date of the text was dropped or read differently than written. */
  VALUE_NOT_UNDERSTOOD: 'WORKFLOW_DESCRIPTION_VALUE_NOT_UNDERSTOOD',
  /** The model's reading does not match the text (words not found in it, a name it never wrote). */
  NOT_UNDERSTOOD: 'WORKFLOW_DESCRIPTION_NOT_UNDERSTOOD',
  /**
   * The operator points the other way from the words: an exclusion ("not equals") with no word of
   * negation, or an upper bound read as a lower bound ("até R$ 5.000" as greater than).
   */
  OPERATOR_NOT_UNDERSTOOD: 'WORKFLOW_DESCRIPTION_OPERATOR_NOT_UNDERSTOOD',
  /** The text names no condition. A rule without conditions would submit every bill. */
  NO_CONDITIONS: 'WORKFLOW_DESCRIPTION_NO_CONDITIONS',
  VENDOR_AMBIGUOUS: 'WORKFLOW_DESCRIPTION_VENDOR_AMBIGUOUS',
  VENDOR_NOT_FOUND: 'WORKFLOW_DESCRIPTION_VENDOR_NOT_FOUND',
  /** The rule tests the vendor, and the caller cannot read vendors (`party:read`). No vendor is read. */
  VENDOR_READ_REQUIRED: 'WORKFLOW_DESCRIPTION_VENDOR_READ_REQUIRED',
  /** A cost center, department, account, segment value or service type matched more than one item. */
  CATALOG_ITEM_AMBIGUOUS: 'WORKFLOW_DESCRIPTION_CATALOG_ITEM_AMBIGUOUS',
  CATALOG_ITEM_NOT_FOUND: 'WORKFLOW_DESCRIPTION_CATALOG_ITEM_NOT_FOUND',
  /**
   * The rule tests a GL account or managerial account without `gl:read`, or a cost center,
   * department or segment without `org:read`. No item of that catalog is read.
   */
  CATALOG_READ_REQUIRED: 'WORKFLOW_DESCRIPTION_CATALOG_READ_REQUIRED',
  /** The save validators refused the draft. The body carries the validator's own code when it has one. */
  DRAFT_INVALID: 'WORKFLOW_DESCRIPTION_DRAFT_INVALID',
  /** The model call failed (provider down, timeout, unreadable answer). Thrown, not a 200 refusal. */
  COMPILER_FAILED: 'WORKFLOW_DESCRIPTION_COMPILER_FAILED',
} as const;
export type WorkflowDescriptionErrorCode =
  (typeof WorkflowDescriptionErrorCode)[keyof typeof WorkflowDescriptionErrorCode];

/** The codes a 200 refusal can carry. COMPILER_FAILED is an error response, not a refusal. */
export type WorkflowDescriptionRejectionCode = Exclude<
  WorkflowDescriptionErrorCode,
  typeof WorkflowDescriptionErrorCode.COMPILER_FAILED
>;

export interface WorkflowDescriptionCopy {
  ptBr: string;
  en: string;
}

export const WORKFLOW_DESCRIPTION_ERROR_MESSAGES: Record<
  WorkflowDescriptionErrorCode,
  WorkflowDescriptionCopy
> = {
  [WorkflowDescriptionErrorCode.UNSUPPORTED]: {
    ptBr: 'Não consigo criar esta regra. Tire as palavras que não consigo usar ou diga de outro jeito.',
    en: 'This rule cannot be created. Remove the words that cannot be used or say it another way.',
  },
  [WorkflowDescriptionErrorCode.OUT_OF_VOCABULARY]: {
    ptBr: 'Não consigo criar esta regra: ela pede algo que uma regra de conta não pode testar ou fazer aqui.',
    en: 'This rule cannot be created: it asks for something a bill rule cannot test or do here.',
  },
  [WorkflowDescriptionErrorCode.VALUE_NOT_UNDERSTOOD]: {
    ptBr: 'Não consegui ler com segurança um número ou uma data do texto. Escreva valores em reais (por exemplo, R$ 5.000,00) e datas como dd/mm/aaaa.',
    en: 'A number or a date in the text could not be read safely. Write amounts in reais (for example, R$ 5.000,00) and dates as dd/mm/yyyy.',
  },
  [WorkflowDescriptionErrorCode.NOT_UNDERSTOOD]: {
    ptBr: 'Não consegui ler esta descrição com segurança. Escreva de outro jeito, com palavras mais diretas.',
    en: 'This description could not be read safely. Say it another way, in more direct words.',
  },
  [WorkflowDescriptionErrorCode.OPERATOR_NOT_UNDERSTOOD]: {
    ptBr: 'Não consegui ler com segurança se a regra inclui ou exclui algo, ou se um valor é limite máximo ou mínimo. Escreva de forma direta, por exemplo «exceto ACME» ou «até R$ 5.000,00».',
    en: 'Whether the rule includes or excludes something, or whether a value is an upper or a lower limit, could not be read safely. Say it directly, for example "except ACME" or "up to R$ 5.000,00".',
  },
  [WorkflowDescriptionErrorCode.NO_CONDITIONS]: {
    ptBr: 'A descrição não diz quais contas a regra deve pegar. Uma regra sem condição enviaria todas as contas para aprovação.',
    en: 'The description does not say which bills the rule should match. A rule without a condition would send every bill to approval.',
  },
  [WorkflowDescriptionErrorCode.VENDOR_AMBIGUOUS]: {
    ptBr: 'O nome do fornecedor corresponde a mais de um fornecedor. Escolha um e tente de novo.',
    en: 'The vendor name matches more than one vendor. Pick one and try again.',
  },
  [WorkflowDescriptionErrorCode.VENDOR_NOT_FOUND]: {
    ptBr: 'Não encontrei um fornecedor com este nome.',
    en: 'No vendor with this name was found.',
  },
  [WorkflowDescriptionErrorCode.VENDOR_READ_REQUIRED]: {
    ptBr: 'Esta regra usa o fornecedor, e você não tem acesso aos fornecedores. Tire o fornecedor da regra ou peça a quem tem acesso.',
    en: 'This rule uses the vendor, and you do not have access to vendors. Remove the vendor from the rule or ask someone who has access.',
  },
  [WorkflowDescriptionErrorCode.CATALOG_ITEM_AMBIGUOUS]: {
    ptBr: 'Este nome corresponde a mais de um item do cadastro. Escolha um e tente de novo.',
    en: 'This name matches more than one catalog item. Pick one and try again.',
  },
  [WorkflowDescriptionErrorCode.CATALOG_READ_REQUIRED]: {
    ptBr: 'Esta regra usa um cadastro que você não tem acesso (conta contábil, conta gerencial, centro de custo, departamento ou segmento). Tire essa parte da regra ou peça a quem tem acesso.',
    en: 'This rule uses a catalog you do not have access to (GL account, managerial account, cost center, department or segment). Remove that part of the rule or ask someone who has access.',
  },
  [WorkflowDescriptionErrorCode.CATALOG_ITEM_NOT_FOUND]: {
    ptBr: 'Não encontrei este item no cadastro da entidade. Use o código ou o nome exato.',
    en: 'This item was not found in the entity catalog. Use the exact code or name.',
  },
  [WorkflowDescriptionErrorCode.DRAFT_INVALID]: {
    ptBr: 'A regra descrita não passa nas validações de regras de workflow.',
    en: 'The described rule does not pass the workflow rule validations.',
  },
  [WorkflowDescriptionErrorCode.COMPILER_FAILED]: {
    ptBr: 'Não foi possível ler a regra agora. Tente de novo em um minuto.',
    en: 'The rule could not be read right now. Try again in a minute.',
  },
};

/**
 * One condition as the compiler reads it, before names become ids. `values` holds what was
 * written (a vendor name, a cost center code) for entity kinds, a canonical value for the rest:
 * `"5000.00"` for an amount, `YYYY-MM-DD` for a date, the registry's own value for an enum,
 * `"true"` / `"false"` for a boolean. It is empty for `is_null` and `is_not_null`.
 */
export interface WorkflowDescriptionCondition {
  /** A field of the vocabulary, or `segment` together with `segmentName`. */
  field: string;
  segmentName: string | null;
  operator: BillWorkflowConditionOperator;
  valueKind: WorkflowDescriptionValueKind;
  values: string[];
  /** The words of the description this condition came from, copied from it. */
  sourceText: string;
}

/** What the compiler agent answers. It never carries ids: resolving names is the service's job. */
export type WorkflowDescriptionCompilerResult =
  | {
      status: 'draft';
      name: string;
      summary: string;
      conditions: WorkflowDescriptionCondition[];
    }
  | {
      status: 'rejected';
      code: WorkflowDescriptionRejectionCode;
      unsupportedParts: string[];
    };

/** An item the name could mean. `detail` is the vendor's tax id, or a code, to tell two apart. */
export interface WorkflowDescriptionCandidate {
  id: string;
  name: string;
  detail: string | null;
}

export interface WorkflowDescriptionRejected {
  status: 'rejected';
  code: WorkflowDescriptionRejectionCode;
  message: string;
  messagePtBr: string;
  /** The words of the description that could not be used. Empty when the refusal is about a name. */
  unsupportedParts: string[];
  /** The name that was looked up (vendor, cost center, ...) when the refusal is about a name. */
  text: string | null;
  /** Up to {@link WORKFLOW_DESCRIPTION_MAX_LISTED} items for an ambiguous name, else empty. */
  candidates: WorkflowDescriptionCandidate[];
  /**
   * For {@link WorkflowDescriptionErrorCode.DRAFT_INVALID}: the stable code the save validator
   * answered with, when it has one. Null for every other refusal.
   */
  validationCode: string | null;
}

/** One condition in plain terms: ids resolved back to the names the person wrote or picked. */
export interface WorkflowProposalReadableCondition {
  conditionIndex: number;
  field: string;
  operator: BillWorkflowConditionOperator;
  values: { value: string; label: string }[];
}

export interface WorkflowProposalReplay {
  /** Bills of the replayed set that the draft would have matched. */
  matched: number;
  /** Bills in the replayed set the caller may see (newest 200 of the entity, the vendor if named). */
  total: number;
  sample: { billId: string; number: string | null }[];
}

export interface WorkflowProposalDraft {
  status: 'draft';
  draft: {
    name: string;
    description: string;
    entityId: string;
    triggerTypeCode: typeof WorkflowTriggerTypeCode.BILL_UPDATED;
    conditions: CreateWorkflowConditionPayload[];
    actions: CreateWorkflowActionPayload[];
  };
  readable: WorkflowProposalReadableCondition[];
  /** The words each condition came from, shown next to it so a loose reading is easy to spot. */
  conditionSources: { conditionIndex: number; sourceText: string }[];
  /**
   * The draft tried on the entity's recent bills. Null when the caller may not read bills
   * (`ap:bill:read`): the replay counts and names bills, so it is never computed for them.
   */
  replay: WorkflowProposalReplay | null;
  /** What the form sends back as `origin` on Save. */
  origin: {
    kind: typeof WorkflowOriginKind.DESCRIPTION;
    text: string;
    sourceBillId: string | null;
  };
}

export type WorkflowProposalResponse = WorkflowProposalDraft | WorkflowDescriptionRejected;

export interface WorkflowProposalRequest {
  description: string;
  entityId: string;
  /** The bill the person is looking at, kept as the origin's source bill. Optional. */
  sourceBillId?: string | null;
  /**
   * A candidate the person picked after an ambiguous refusal: the text it answers and the id. The
   * name is then taken as resolved, so the same text is not asked about twice.
   */
  resolutions?: { text: string; id: string }[];
}
